/* ==========================================================================
   NOOKISM, isometric engine
   The Shapeshift panel system, drawn true. Unit 1 = a 35cm panel.
   Exposes window.NK = { isoArt, KIT, kits, look, configs }.
   Ported from the nookism brand reference; material palette is intentionally
   a neutral grey set (white theme) and is independent of the themeable UI tokens.
   ========================================================================== */
(function(){
'use strict';
const T=15, TH=0.075;
const PANEL={top:'#FAFAF8',rt:'#DDDDD8',lf:'#B6B6AE'};
const SOFT ={top:'#E9E9E4',rt:'#C4C4BC',lf:'#9C9C92'};
function PR(x,y,z){return [(x-y)*T,(x+y)*T*0.5-z*T];}
function ps(a){return a.map(p=>p[0].toFixed(1)+','+p[1].toFixed(1)).join(' ');}

function boxFaces(x,y,z,w,d,h,soft){
  const c=soft?SOFT:PANEL,P=(a,b,e)=>PR(x+a,y+b,z+e);
  return {depth:(x+w/2)+(y+d/2)+(z+h/2),poly:[
    [[P(0,d,0),P(w,d,0),P(w,d,h),P(0,d,h)],c.lf],
    [[P(w,0,0),P(w,d,0),P(w,d,h),P(w,0,h)],c.rt],
    [[P(0,0,h),P(w,0,h),P(w,d,h),P(0,d,h)],c.top]]};
}
function triFaces(x,y,z,lA,lB,t,soft,pl){
  const c=soft?SOFT:PANEL,P=(a,b,e)=>PR(x+a,y+b,z+e);
  let A1,B1,C1,A2,B2,C2,dep,cap;
  if(pl==='yz'){A1=P(0,0,0);B1=P(0,lA,0);C1=P(0,0,lB);
    A2=P(t,0,0);B2=P(t,lA,0);C2=P(t,0,lB);dep=(x+t/2)+(y+lA/3)+(z+lB/3);cap=c.lf;}
  else if(pl==='xy'){
    // flat/horizontal wedge (a corner shelf): spans x (lA) and y (lB), thickness
    // t runs up in z. The top face is what reads in the iso view, so it gets
    // the brightest tone (c.top), same as a deck's upward face
    A1=P(0,0,0);B1=P(lA,0,0);C1=P(0,lB,0);
    A2=P(0,0,t);B2=P(lA,0,t);C2=P(0,lB,t);dep=(x+lA/3)+(y+lB/3)+(z+t/2);cap=c.top;}
  else{A1=P(0,0,0);B1=P(lA,0,0);C1=P(0,0,lB);
    A2=P(0,t,0);B2=P(lA,t,0);C2=P(0,t,lB);dep=(x+lA/3)+(y+t/2)+(z+lB/3);cap=c.lf;}
  // the near face [A1,B1,C1] is drawn LAST, exactly on the lattice plane, so
  // in stroked (wireframe) renders every triangle edge lands dead-centre on
  // the connector circles; the offset prism faces only add the 3D thickness
  return {depth:dep,poly:[
    [[A1,B1,B2,A2],c.rt],[[A1,C1,C2,A2],c.rt],[[A2,B2,C2],cap],[[A1,B1,C1],cap]]};
}
const WIRE='#C9C9C1';
function gridFaces(x,y,z,pl){
  // wire-grid panel, the fourth cut in the system: a 1×1 mesh of thin wires
  // (4×4 cells) drawn as slim in-plane quads. pl 'y' = plane y const spanning
  // x/z; pl 'x' = plane x const spanning y/z. Edge wires double as the frame.
  const w=0.018,out=[],N=4;
  const pt=pl==='y'?((u,v)=>PR(x+u,y,z+v)):((u,v)=>PR(x,y+u,z+v));
  for(let n=0;n<=N;n++){
    const f=n/N;
    out.push([[pt(f-w,0),pt(f+w,0),pt(f+w,1),pt(f-w,1)],WIRE]);
    out.push([[pt(0,f-w),pt(1,f-w),pt(1,f+w),pt(0,f+w)],WIRE]);
  }
  const dep=pl==='y'?(x+0.5)+y+(z+0.5):x+(y+0.5)+(z+0.5);
  return {depth:dep,poly:out};
}
function itemFaces(p){
  const t=p[0];
  if(t==='h')return boxFaces(p[1],p[2],p[3],1,1,TH,false);
  if(t==='x')return boxFaces(p[1],p[2],p[3],TH,1,1,false);
  if(t==='y')return boxFaces(p[1],p[2],p[3],1,TH,1,false);
  // half panels (17.5cm, half the height of a full 'x'/'y' panel) — the third
  // cut alongside square panels and triangles, per the Shapeshift panel story
  if(t==='xh')return boxFaces(p[1],p[2],p[3],TH,1,0.5,false);
  if(t==='yh')return boxFaces(p[1],p[2],p[3],1,TH,0.5,false);
  if(t==='gx')return gridFaces(p[1],p[2],p[3],'x');
  if(t==='gy')return gridFaces(p[1],p[2],p[3],'y');
  if(t==='b')return boxFaces(p[1],p[2],p[3],p[4],p[5],p[6],false);
  if(t==='s')return boxFaces(p[1],p[2],p[3],p[4],p[5],p[6],true);
  if(t==='T')return triFaces(p[1],p[2],p[3],p[4],p[5],p[6],p[7]==='s',p[8]||'xz');
  return null;
}
function itemCorners(p){
  const t=p[0],i=p[1],j=p[2],k=p[3];
  if(t==='h')return [[i,j,k],[i+1,j,k],[i+1,j+1,k],[i,j+1,k]];
  if(t==='x'||t==='gx')return [[i,j,k],[i,j+1,k],[i,j+1,k+1],[i,j,k+1]];
  if(t==='y'||t==='gy')return [[i,j,k],[i+1,j,k],[i+1,j,k+1],[i,j,k+1]];
  if(t==='xh')return [[i,j,k],[i,j+1,k],[i,j+1,k+0.5],[i,j,k+0.5]];
  if(t==='yh')return [[i,j,k],[i+1,j,k],[i+1,j,k+0.5],[i,j,k+0.5]];
  if(t==='T'){
    // triangles connect like every other panel: a dot at each of the three
    // vertices (both base corners + apex), so all edges meet at circle centres
    const lA=p[4],lB=p[5],pl=p[8]||'xz';
    if(pl==='yz')return [[i,j,k],[i,j+lA,k],[i,j,k+lB]];
    if(pl==='xy')return [[i,j,k],[i+lA,j,k],[i,j+lB,k]];
    return [[i,j,k],[i+lA,j,k],[i,j,k+lB]];
  }
  return [];
}
function connDot(x,y,z,r,shape){
  return {depth:x+y+z+1.1,conn:{p:PR(x,y,z),r:r*T,shape:shape||'ball'}};
}

function isoArt(items,vw,vh,autoConn){
  vw=vw||240;vh=vh||220;
  if(autoConn===undefined)autoConn=true;
  const built=[];
  items.forEach(p=>{
    if(p[0]==='C'){built.push(connDot(p[1],p[2],p[3],p[4]||0.22,p[5]));return;}
    const f=itemFaces(p);if(f)built.push(f);
  });
  if(autoConn){
    const seen={};
    items.forEach(p=>{
      if(p[0]!=='h'&&p[0]!=='x'&&p[0]!=='y'&&p[0]!=='xh'&&p[0]!=='yh'&&p[0]!=='gx'&&p[0]!=='gy'&&p[0]!=='T')return;
      itemCorners(p).forEach(v=>{
        const key=v[0]+'|'+v[1]+'|'+v[2];
        if(!seen[key]){seen[key]=1;built.push(connDot(v[0],v[1],v[2],0.085));}
      });
    });
  }
  built.sort((a,b)=>a.depth-b.depth);
  let mnx=1e9,mny=1e9,mxx=-1e9,mxy=-1e9;
  const ext=(x,y)=>{if(x<mnx)mnx=x;if(x>mxx)mxx=x;if(y<mny)mny=y;if(y>mxy)mxy=y;};
  built.forEach(o=>{
    if(o.poly)o.poly.forEach(f=>f[0].forEach(pt=>ext(pt[0],pt[1])));
    if(o.conn){ext(o.conn.p[0]-o.conn.r,o.conn.p[1]-o.conn.r);
      ext(o.conn.p[0]+o.conn.r,o.conn.p[1]+o.conn.r);}
  });
  const pad=18,s=Math.min((vw-pad*2)/(mxx-mnx),(vh-pad*2)/(mxy-mny));
  const tx=pad-mnx*s+((vw-pad*2)-(mxx-mnx)*s)/2;
  const ty=pad-mny*s+((vh-pad*2)-(mxy-mny)*s)/2;
  const shX=(mnx+mxx)/2,shW=(mxx-mnx)*0.56;
  let body=`<g class="shadow"><ellipse cx="${shX.toFixed(1)}" cy="${(mxy+4).toFixed(1)}" rx="${shW.toFixed(1)}" ry="${(shW*0.22).toFixed(1)}" fill="#111110" opacity="0.1"/></g>`;
  built.forEach((o,i)=>{
    body+=`<g class="pc" style="--i:${i}">`;
    if(o.poly)body+=o.poly.map(f=>`<polygon points="${ps(f[0])}" fill="${f[1]}"/>`).join('');
    if(o.conn){const cn=o.conn;
      if(cn.shape==='dome')body+=`<path class="conn" d="M${(cn.p[0]-cn.r).toFixed(1)} ${cn.p[1].toFixed(1)} A ${cn.r.toFixed(1)} ${cn.r.toFixed(1)} 0 0 1 ${(cn.p[0]+cn.r).toFixed(1)} ${cn.p[1].toFixed(1)} Z"/>`;
      else body+=`<circle class="conn" cx="${cn.p[0].toFixed(1)}" cy="${cn.p[1].toFixed(1)}" r="${cn.r.toFixed(1)}"/>`;
    }
    body+=`</g>`;
  });
  return `<svg viewBox="0 0 ${vw} ${vh}" aria-hidden="true" focusable="false"><g transform="translate(${tx.toFixed(1)} ${ty.toFixed(1)}) scale(${s.toFixed(3)})">${body}</g></svg>`;
}

/* kit specs: assembled size + parts list, derived straight from the build
   data. One module pitch = 36.5cm (a 35cm panel seated in its connectors,
   per the assembled-cube spec), so sizes match what arrives in the box. */
const PITCH=36.5;
function kitSpecs(id){
  const items=KIT[id]; if(!items) return null;
  let mnx=1e9,mny=1e9,mnz=1e9,mxx=-1e9,mxy=-1e9,mxz=-1e9;
  const counts={sq:0,half:0,tri:0,grid:0};
  const seen={}; let conns=0;
  items.forEach(p=>{
    const t=p[0]; let ext=null;
    if(t==='h'){counts.sq++;ext=[p[1],p[2],p[3],p[1]+1,p[2]+1,p[3]];}
    else if(t==='x'){counts.sq++;ext=[p[1],p[2],p[3],p[1],p[2]+1,p[3]+1];}
    else if(t==='y'){counts.sq++;ext=[p[1],p[2],p[3],p[1]+1,p[2],p[3]+1];}
    else if(t==='xh'){counts.half++;ext=[p[1],p[2],p[3],p[1],p[2]+1,p[3]+0.5];}
    else if(t==='yh'){counts.half++;ext=[p[1],p[2],p[3],p[1]+1,p[2],p[3]+0.5];}
    else if(t==='gx'){counts.grid++;ext=[p[1],p[2],p[3],p[1],p[2]+1,p[3]+1];}
    else if(t==='gy'){counts.grid++;ext=[p[1],p[2],p[3],p[1]+1,p[2],p[3]+1];}
    else if(t==='T'){counts.tri++;const pl=p[8]||'xz',lA=p[4],lB=p[5];
      if(pl==='yz')ext=[p[1],Math.min(p[2],p[2]+lA),p[3],p[1],Math.max(p[2],p[2]+lA),p[3]+lB];
      else if(pl==='xy')ext=[Math.min(p[1],p[1]+lA),Math.min(p[2],p[2]+lB),p[3],Math.max(p[1],p[1]+lA),Math.max(p[2],p[2]+lB),p[3]];
      else ext=[Math.min(p[1],p[1]+lA),p[2],p[3],Math.max(p[1],p[1]+lA),p[2],p[3]+lB];}
    if(ext){
      mnx=Math.min(mnx,ext[0]);mny=Math.min(mny,ext[1]);mnz=Math.min(mnz,ext[2]);
      mxx=Math.max(mxx,ext[3]);mxy=Math.max(mxy,ext[4]);mxz=Math.max(mxz,ext[5]);
    }
    itemCorners(p).forEach(v=>{const k=v[0]+'|'+v[1]+'|'+v[2];if(!seen[k]){seen[k]=1;conns++;}});
  });
  const panels=counts.sq+counts.half+counts.tri+counts.grid;
  return {
    units:{w:mxx-mnx,d:mxy-mny,h:mxz-mnz},
    cm:{w:Math.round((mxx-mnx)*PITCH),d:Math.round((mxy-mny)*PITCH),h:Math.round((mxz-mnz)*PITCH)},
    counts, conns, panels,
    kg: Math.round((panels*0.15 + conns*0.02)*10)/10
  };
}

/* builders: lay panels on the 35cm grid */
function wallY(a,i0,j0,k0,w,h){for(let i=i0;i<i0+w;i++)for(let k=k0;k<k0+h;k++)a.push(['y',i,j0,k]);}
function wallX(a,i0,j0,k0,d,h){for(let j=j0;j<j0+d;j++)for(let k=k0;k<k0+h;k++)a.push(['x',i0,j,k]);}
function deck(a,i0,j0,k0,w,d){for(let i=i0;i<i0+w;i++)for(let j=j0;j<j0+d;j++)a.push(['h',i,j,k0]);}

/* ===== the kits. Discrete panels on an integer grid ===== */
const KIT={};
let a;

/* paddock: pet pen. Mesh top row on the show side (see-through, like the real
   wire pens), a step-over entry on the right, castle ears + half battlement */
a=[]; deck(a,0,0,0,3,3); wallY(a,0,0,0,3,2); wallX(a,0,0,0,3,2);
for(let i=0;i<3;i++){a.push(['y',i,3,0],['gy',i,3,1]);}
a.push(['x',3,0,0],['x',3,0,1],['x',3,2,0],['x',3,2,1]);
a.push(['xh',3,1,0]); /* half-height step-over threshold in the entry gap */
a.push(['s',0.95,0.85,TH,1.2,1.0,0.5],['s',0.78,1.05,TH+0.42,0.6,0.6,0.42]);
a.push(['T',0,0,2,1,1,TH,'','yz'],['T',0,3,2,-1,1,TH,'','yz']); /* castle ears, vertical edge out */
a.push(['xh',0,1,2]); /* half battlement between the ears */
KIT.paddock=a;

/* lookout: cat tower. Climbing mesh up the back, slanted cap, ground ramp
   that lands exactly on the first perch, guard rail on the high perch */
a=[]; wallX(a,0,0,0,1,3); wallX(a,1,0,0,1,3); wallY(a,0,0,0,1,3);
a.push(['gy',0,1,0],['gy',0,1,1],['gy',0,1,2]);
deck(a,0,0,0,1,1); deck(a,0,0,1,1,1); deck(a,0,0,2,1,1); deck(a,0,0,3,1,1);
deck(a,1,0,1,1,1); deck(a,-1,0,2,1,1);
a.push(['s',0.15,0.15,3+TH,0.7,0.6,0.42],['s',0.12,0.26,3+TH+0.42,0.4,0.4,0.34]);
a.push(['T',0,0,3,1,1,TH,'','xz']); /* slanted roof cap */
a.push(['T',1,0,0,1,1,TH,'','xz']); /* ramp up to the first perch */
a.push(['yh',-1,0,2]); /* half guard rail on the high perch */
KIT.lookout=a;

/* cubby: toy house. Symmetric trapezoid roof (triangle, square, triangle),
   a corner shelf floating in one bay, a half toy-fence on the mid shelf */
a=[]; wallX(a,0,0,0,1,2); wallX(a,1,0,0,1,2); wallX(a,2,0,0,1,2); wallX(a,3,0,0,1,2);
wallY(a,0,0,0,3,2); deck(a,0,0,0,3,1); deck(a,0,0,1,3,1); deck(a,0,0,2,3,1);
a.push(['s',0.2,0.25,TH,0.62,0.55,0.62],['s',1.25,0.25,1+TH,0.55,0.5,0.55],['s',2.22,0.28,TH,0.55,0.5,0.5]);
a.push(['T',1,0,2,-1,1,TH,'','xz'],['y',1,0,2],['T',2,0,2,1,1,TH,'','xz']);
a.push(['T',1,0,1.5,-1,1,TH,'','xy']); /* corner shelf nested in the left bay */
a.push(['yh',0,1,1]); /* half toy-fence on the mid shelf */
KIT.cubby=a;

/* hideout: the flagship house. Trapezoid gables both ends, doorway with a
   mesh transom window above it, roof deck, side parapet */
a=[]; wallY(a,0,0,0,3,2); wallX(a,0,0,0,3,2); wallX(a,3,0,0,3,2);
a.push(['y',0,3,0],['y',0,3,1],['gy',1,3,1],['y',2,3,0],['y',2,3,1]);
a.push(['T',0,1,2,-1,1,TH,'','yz'],['x',0,1,2],['T',0,2,2,1,1,TH,'','yz']);
a.push(['T',3,1,2,-1,1,TH,'','yz'],['x',3,1,2],['T',3,2,2,1,1,TH,'','yz']);
deck(a,0,1,3,3,1);
a.push(['yh',0,0,2]); KIT.hideout=a;

/* backdrop: photo frame. Triangle buttress feet brace the wall on the deck,
   castle ears + half crenel across the top */
a=[]; wallY(a,0,1,0,3,4); deck(a,0,0,0,3,1);
a.push(['T',0,1,0,-1,1,TH,'','yz'],['T',3,1,0,-1,1,TH,'','yz']); /* buttress feet */
a.push(['T',0,1,4,1,1,TH,'','xz'],['T',3,1,4,-1,1,TH,'','xz']); /* castle ears */
a.push(['yh',1,1,4]); /* half crenel between them */
KIT.backdrop=a;

/* screen: room divider. The middle fold is light wire mesh, corner shelves
   nest into both elbows, half riser + pennant crown the tall fold */
a=[]; wallY(a,0,0,0,2,3); wallY(a,2,2,0,2,3);
for(let j=0;j<2;j++)for(let k=0;k<3;k++)a.push(['gx',2,j,k]);
a.push(['b',0,0,3,2,TH,0.5],['b',2,0,3,TH,2,0.5],['b',2,2,3,2,TH,0.5]);
a.push(['C',0,0,3,0.085],['C',2,0,3,0.085],['C',2,2,3,0.085],['C',4,2,3,0.085]);
a.push(['C',2,0,1,0.1,'dome'],['C',2,0,2,0.1,'dome'],['C',2,2,1,0.1,'dome'],['C',2,2,2,0.1,'dome']);
a.push(['yh',2,2,3.5],['T',2,2,4,1,1,TH,'','xz']); /* half riser + pennant */
a.push(['T',2,0,1,-1,1,TH,'','xy'],['T',2,2,2,1,-1,TH,'','xy']); /* elbow corner shelves */
KIT.screen=a;

/* stacks: bookshelf with a castle top — ears at both ends, half battlement
   between, a half book-fence holding the mid shelf row */
a=[]; wallX(a,0,0,0,1,3); wallX(a,3,0,0,1,3); wallY(a,0,0,0,3,3);
deck(a,0,0,0,3,1); deck(a,0,0,1,3,1); deck(a,0,0,2,3,1); deck(a,0,0,3,3,1);
a.push(['s',0.25,0.35,TH,0.16,0.6,0.78],['s',0.43,0.35,TH,0.16,0.6,0.72],
       ['s',0.61,0.35,TH,0.16,0.6,0.8],['s',2.2,0.35,1+TH,0.16,0.6,0.7],
       ['s',2.38,0.35,1+TH,0.16,0.6,0.76]);
a.push(['T',0,0.35,2,1,1,TH,'','xz'],['T',3,0.35,2,-1,1,TH,'','xz']); /* angled book-lean panels in the top bay corners */
a.push(['T',3,0,0.5,-1,1,TH,'','xy']); /* flat corner shelf in the bottom-right bay */
a.push(['yh',1,1,1]); /* half book-fence on the mid shelf */
KIT.stacks=a;

/* lean: ladder plant shelf. Triangle gussets bridge the step diagonally on
   both sides — the lean, drawn literally — plus a half plant-fence on top */
a=[]; wallY(a,0,0,0,3,3); wallX(a,0,0,0,1,3); a.push(['x',0,1,0]);
wallX(a,3,0,0,1,3); a.push(['x',3,1,0]);
deck(a,0,0,1,3,2); deck(a,0,0,2,3,1); deck(a,0,0,3,3,1);
a.push(['s',0.4,0.4,1+TH,0.5,0.46,0.4],['s',0.5,0.5,1+TH+0.4,0.3,0.3,0.34]);
a.push(['s',1.6,0.3,2+TH,0.46,0.4,0.4],['s',1.68,0.4,2+TH+0.4,0.3,0.28,0.32]);
a.push(['s',0.55,0.3,3+TH,0.5,0.4,0.42]);
a.push(['T',0,1,1,1,1,TH,'','yz'],['T',3,1,1,1,1,TH,'','yz']); /* step gussets */
a.push(['yh',1,1,3]); /* half plant-fence on the top step */
KIT.lean=a;

/* vitrine: collector's cabinet. Mesh middle column at the back (hang cards
   and charms off it), house peak on top, corner shelves in two bays */
a=[]; wallX(a,0,0,0,1,3); wallX(a,1,0,0,1,3); wallX(a,2,0,0,1,3); wallX(a,3,0,0,1,3);
for(let k=0;k<3;k++){a.push(['y',0,0,k],['gy',1,0,k],['y',2,0,k]);}
deck(a,0,0,0,3,1); deck(a,0,0,1,3,1); deck(a,0,0,2,3,1); deck(a,0,0,3,3,1);
a.push(['s',0.3,0.3,TH,0.42,0.4,0.5],['s',1.32,0.32,1+TH,0.38,0.36,0.45],
       ['s',2.28,0.3,2+TH,0.4,0.38,0.45],['s',0.32,0.32,2+TH,0.36,0.34,0.4]);
a.push(['T',0,0.35,2,1,1,TH,'','xz'],['T',3,0.35,2,-1,1,TH,'','xz']); /* angled display panels in the top bay's outer corners */
a.push(['T',0,0,1.5,1,1,TH,'','xy'],['T',3,0,1.5,-1,1,TH,'','xy']); /* flat corner display shelves in the mid bays */
a.push(['yh',1,1,1]); KIT.vitrine=a;

/* booth: exhibitor booth. Mesh banner panels top-centre of the back wall,
   castle ears on the header ends, half display risers on the counter */
a=[]; for(let i=0;i<4;i++)for(let k=0;k<3;k++){if(k===2&&(i===1||i===2))a.push(['gy',i,0,k]);else a.push(['y',i,0,k]);}
wallX(a,0,0,0,2,3); wallX(a,4,0,0,2,3);
deck(a,0,0,0,4,2);
deck(a,1,1,1,2,1); wallY(a,1,1,0,2,1); wallY(a,1,2,0,2,1);
wallX(a,1,1,0,1,1); wallX(a,3,1,0,1,1);
a.push(['b',0,0,3,4,TH,0.5]);
a.push(['C',0,0,3,0.085],['C',2,0,3,0.085],['C',4,0,3,0.085]);
a.push(['T',0,0,3.5,1,1,TH,'','xz'],['T',4,0,3.5,-1,1,TH,'','xz']); /* header ears */
a.push(['xh',1,1,1],['xh',3,1,1]); /* half risers on the counter */
KIT.booth=a;

a=[]; deck(a,0,0,0,3,2); deck(a,0,0,1,3,2); wallY(a,0,0,0,3,1); wallY(a,0,2,0,3,1);
wallX(a,0,0,0,2,1); wallX(a,3,0,0,2,1); wallY(a,0,0,1,3,1); deck(a,0,0,2,3,1);
a.push(['s',0.4,0.5,1+TH,0.55,0.5,0.5],['s',1.55,0.5,1+TH,0.55,0.5,0.5],
       ['s',0.9,0.25,2+TH,0.6,0.5,0.52]);
a.push(['b',0,0,1,3,TH,0.5]);
a.push(['C',0,1,2,0.085],['C',3,1,2,0.085]);
a.push(['T',1,0,2,-1,1,TH,'','xz'],['T',1,0,2,1,1,TH,'','xz']); /* awning peak, both triangles meeting at the x=1 ridge */
a.push(['yh',2,0,2]); /* half sign block beside it */
KIT.stall=a;

a=[]; wallX(a,0,0,0,1,2); wallX(a,4,0,0,1,2); wallY(a,0,0,0,4,2);
deck(a,0,0,0,4,1); deck(a,0,0,1,4,1); deck(a,0,0,2,4,1);
a.push(['s',0.3,0.2,TH,0.7,0.55,0.3],['s',1.3,0.2,TH,0.7,0.55,0.3],
       ['s',2.4,0.2,1+TH,0.7,0.55,0.3],['s',0.5,0.25,1+TH,0.7,0.55,0.3]);
a.push(['T',0,0,1.5,1,1,TH,'','xy'],['T',4,0,1.5,-1,1,TH,'','xy']); /* flat corner shelves in the mid bay, for keys and mail */
a.push(['yh',1,1,1],['yh',2,1,1]); /* half shoe-fences along the mid shelf */
KIT.landing=a;

/* pegwall: the whole wall is wire mesh (a true pegboard), with triangle
   buttress feet, cantilevered triangle ledges and a half crest */
a=[]; for(let i=0;i<3;i++)for(let k=0;k<3;k++)a.push(['gy',i,0,k]);
a.push(['s',0.5,TH,1.0,0.28,0.32,0.26],['s',1.9,TH,1.0,0.28,0.32,0.26],
       ['s',1.2,TH,2.1,0.28,0.32,0.26]);
a.push(['s',0.25,TH+0.32,0.55,0.78,0.46,0.78],['s',1.62,TH+0.32,1.65,0.78,0.46,0.78]);
a.push(['T',0,0,0,1,1,TH,'','yz'],['T',3,0,0,1,1,TH,'','yz']); /* buttress feet */
a.push(['T',0,0,1,1,1,TH,'','xy'],['T',2,0,2,1,1,TH,'','xy']); /* cantilevered ledges */
a.push(['yh',1,0,3]); /* half crest on top */
KIT.pegwall=a;

/* wardrobe: open armoire. Mesh top row at the back for hanging accessories,
   full-width house peak, half divider on the shoe shelf */
a=[]; wallX(a,0,0,0,1,4); wallX(a,2,0,0,1,4);
for(let i=0;i<2;i++)for(let k=0;k<4;k++){if(k===3)a.push(['gy',i,0,k]);else a.push(['y',i,0,k]);}
deck(a,0,0,0,2,1); deck(a,0,0,2,2,1); deck(a,0,0,4,2,1);
a.push(['s',0.2,0.45,3.35,1.6,0.12,0.12]);
a.push(['s',0.45,0.32,2.45,0.5,0.5,0.85],['s',0.98,0.32,2.5,0.4,0.5,0.5]);
a.push(['T',2,0.32,3,-1,1,TH,'','xz']); /* angled display panel in the upper-right bay, the bag alcove */
a.push(['T',0,0,1,1,1,TH,'','xy']); /* flat corner shelf low in the hanging bay */
a.push(['xh',1,0,2]); /* half divider on the shoe shelf */
KIT.wardrobe=a;

/* mudroom: entry organiser. The hook row is wire mesh (hooks clip to grid,
   exactly like the real product), porch peak + half sign block on top */
a=[]; for(let i=0;i<3;i++)for(let k=0;k<4;k++){if(k===2)a.push(['gy',i,0,k]);else a.push(['y',i,0,k]);}
deck(a,0,1,1,3,1); wallX(a,0,1,0,1,1); wallX(a,3,1,0,1,1);
a.push(['s',0.45,TH,2.0,0.24,0.3,0.24],['s',1.4,TH,2.0,0.24,0.3,0.24],
       ['s',2.35,TH,2.0,0.24,0.3,0.24]);
deck(a,0,1,3,3,1); deck(a,0,1,4,3,1);
wallX(a,0,1,3,1,1); wallX(a,1,1,3,1,1); wallX(a,2,1,3,1,1); wallX(a,3,1,3,1,1);
a.push(['T',0,1.35,3,1,1,TH,'','xz'],['T',3,1.35,3,-1,1,TH,'','xz']); /* angled corner panels in the outer upper cubbies */
a.push(['yh',0,0,4]); /* half sign block on the wall top */
KIT.mudroom=a;

a=[]; wallX(a,0,0,0,1,2); wallX(a,2,0,0,1,2); wallY(a,0,0,0,2,2);
deck(a,0,0,0,2,1); deck(a,0,0,1,2,1); deck(a,0,0,2,2,1);
a.push(['s',0.25,0.3,TH,0.5,0.45,0.5],['s',1.2,0.3,1+TH,0.46,0.42,0.45]);
a.push(['T',0,0,1.5,1,1,TH,'','xy'],['T',2,0,0.5,-1,1,TH,'','xy']); /* flat corner shelves tucked into two bays */
a.push(['yh',1,1,1]); /* half lip on the mid shelf */
KIT.riser=a;

a=[]; wallY(a,0,0,0,4,2);
deck(a,-1,0,0,1,1); deck(a,-1,0,1,1,1); wallX(a,-1,0,0,1,1); wallX(a,0,0,0,1,1);
wallY(a,-1,0,0,1,1); wallY(a,-1,1,0,1,1);
deck(a,4,0,0,1,1); deck(a,4,0,1,1,1); wallX(a,4,0,0,1,1); wallX(a,5,0,0,1,1);
wallY(a,4,0,0,1,1); wallY(a,4,1,0,1,1);
a.push(['T',-1,0,1+TH,1,1,TH,'','xy'],['T',5,0,1+TH,-1,1,TH,'','xy']); /* flat corner trays on the nightstand tops */
a.push(['yh',1,0,2],['yh',2,0,2]); /* half reading ledge along the headboard top */
KIT.headboard=a;

a=[]; wallX(a,0,0,0,1,2); wallX(a,1,0,0,1,2); wallX(a,2,0,0,1,2); wallX(a,3,0,0,1,2);
wallY(a,0,0,0,3,2); deck(a,0,0,0,3,1); deck(a,0,0,1,3,1); deck(a,0,0,2,3,1);
for(let n=0;n<5;n++)a.push(['s',0.18+n*0.13,0.25,TH,0.1,0.7,0.6]);
for(let n=0;n<4;n++)a.push(['s',1.2+n*0.13,0.25,TH,0.1,0.7,0.62]);
for(let n=0;n<5;n++)a.push(['s',2.13+n*0.13,0.25,1+TH,0.1,0.7,0.58]);
a.push(['T',1,0.35,0,-1,1,TH,'','xz'],['T',3,0.35,2,-1,1,TH,'','xz']); /* record lean-wedges, one in a bay and one on top */
a.push(['yh',0,1,0]); /* half lip keeping the front row upright */
KIT.crate=a;

a=[]; wallX(a,0,0,0,1,4); wallX(a,2,0,0,1,4);
deck(a,0,0,0,2,1); deck(a,0,0,1,2,1); deck(a,0,0,2,2,1); deck(a,0,0,3,2,1); deck(a,0,0,4,2,1);
a.push(['s',0.35,0.3,TH,0.42,0.4,0.4],['s',0.42,0.4,TH+0.4,0.28,0.26,0.32]);
a.push(['s',1.2,0.3,1+TH,0.42,0.4,0.4]);
a.push(['s',0.4,0.3,2+TH,0.42,0.4,0.4],['s',0.47,0.4,2+TH+0.4,0.28,0.26,0.3]);
a.push(['s',1.15,0.3,3+TH,0.42,0.4,0.4]);
a.push(['gy',0,1,2],['gy',1,1,2],['gy',0,1,3],['gy',1,1,3]); /* climbing trellis behind the upper tiers */
a.push(['T',0,0,4,1,1,TH,'','xz'],['T',0,0,4,1,1,TH,'','yz']); /* slanted corner sail on the top tier, catching the light */
a.push(['yh',0,1,2],['yh',1,1,3]); /* half plant-guards staggered on the middle tiers */
KIT.nursery=a;

/* storefront: pop-up shop. Mesh middle column at the back for hanging stock,
   parapet ears + half sign band across the top */
a=[]; for(let k=0;k<4;k++){a.push(['y',0,0,k],['gy',1,0,k],['y',2,0,k]);}
wallX(a,0,0,1,1,3); wallX(a,3,0,1,1,3);
deck(a,0,0,1,3,1); deck(a,0,0,2,3,1); deck(a,0,0,3,3,1); deck(a,0,0,4,3,1);
a.push(['s',0.4,0.3,1+TH,0.45,0.4,0.5],['s',1.9,0.3,2+TH,0.42,0.38,0.45],
       ['s',1.0,0.3,3+TH,0.4,0.36,0.42]);
a.push(['T',0,0,4,1,1,TH,'','xz'],['T',3,0,4,-1,1,TH,'','xz']); /* parapet ears */
a.push(['yh',1,0,4]); /* half sign band between them */
KIT.storefront=a;

/* net: portable sports net. A cube tower each side, wire mesh strung
   between them — driveway badminton, packed flat by dinner */
a=[]; wallX(a,0,0,0,1,3); wallX(a,1,0,0,1,3); wallY(a,0,0,0,1,3); wallY(a,0,1,0,1,3);
deck(a,0,0,0,1,1); deck(a,0,0,1,1,1); deck(a,0,0,2,1,1); deck(a,0,0,3,1,1);
wallX(a,4,0,0,1,3); wallX(a,5,0,0,1,3); wallY(a,4,0,0,1,3); wallY(a,4,1,0,1,3);
deck(a,4,0,0,1,1); deck(a,4,0,1,1,1); deck(a,4,0,2,1,1); deck(a,4,0,3,1,1);
a.push(['gy',1,0,1],['gy',2,0,1],['gy',3,0,1]); /* the net itself: wire mesh, clear of the ground */
a.push(['gy',1,0,2],['gy',2,0,2],['gy',3,0,2]);
a.push(['T',1,0.3,0,1,1,TH,'','xz'],['T',4,0.3,0,-1,1,TH,'','xz']); /* diagonal tension struts bracing each post */
a.push(['yh',0,0,3]); /* half scoreboard on the left tower */
a.push(['s',2.28,0.25,0,0.36,0.36,0.36]); /* the ball, waiting under the net */
KIT.net=a;

a=[]; deck(a,0,0,0,2,2); deck(a,0,0,2,2,2); wallY(a,0,0,0,2,2); wallY(a,0,2,0,2,2);
wallX(a,0,0,0,2,2); wallX(a,2,0,0,2,2);
a.push(['T',0,1,2,-1,1,TH,'','yz'],['T',0,1,2,1,1,TH,'','yz']); /* front gable, ridge on the y=1 line */
a.push(['T',2,1,2,-1,1,TH,'','yz'],['T',2,1,2,1,1,TH,'','yz']); /* matching back gable — the cube becomes a little house */
KIT.cube=a;

/* system diagrams (no auto connectors) */
KIT.sysPanels=[['b',0,0,0,1,1,TH],['b',1.7,0,0,0.5,1,TH],['T',0,1.7,0,1,1,TH]];
KIT.sysConnector=[
  ['b',0,0,0,TH,1.6,2],['b',0,0,0,1.6,TH,2],
  ['C',0,0,2,0.26,'dome'],['C',0,0,1,0.24,'dome'],
  ['C',1.9,1.9,0.5,0.34,'ball'],['b',1.65,1.6,0,0.6,0.5,0.32]];
KIT.sysConfig=[['b',0,0,0,1,1,1],['b',0,0,1,1,1,1],['b',1,0,0,1,1,1],
  ['s',0,1.7,0,0.9,0.9,0.9]];

/* kit catalogue. Segment story for the broader Nookism brand */
const kits=[
 {id:'paddock',name:'Paddock',tag:'Pet playpen',price:'from $189',
  desc:'A low panel enclosure that folds flat when the zoomies are done.'},
 {id:'lookout',name:'Lookout',tag:'Cat tower',price:'from $209',
  desc:'Stacked cubes and perches that give a cat the high ground.'},
 {id:'cubby',name:'Cubby',tag:'Kids toy storage',price:'from $159',
  desc:'Open cubbies at a child’s height, so tidying up becomes a game.'},
 {id:'hideout',name:'Hideout',tag:'Kids play fort',price:'from $179',
  desc:'A panelled fort with a real doorway and a roof to hide under.'},
 {id:'backdrop',name:'Backdrop',tag:'Photo & content backdrop',price:'from $129',
  desc:'A clean, even wall on a panel foot, ready to shoot against.'},
 {id:'screen',name:'The Screen',tag:'Room divider',price:'from $229',
  desc:'A folding panel screen that draws a room without building a wall.'},
 {id:'stacks',name:'Stacks',tag:'Open bookshelf',price:'from $189',
  desc:'Grid shelving that grows one 35cm cube at a time.'},
 {id:'lean',name:'The Lean',tag:'Ladder plant shelf',price:'from $149',
  desc:'A stepped open shelf that lifts each plant toward the light.'},
 {id:'vitrine',name:'Vitrine',tag:'Collectibles display cabinet',price:'from $219',
  desc:'A fine grid of compartments sized for figures and small treasures.'},
 {id:'booth',name:'Booth',tag:'Trade show booth',price:'from $349',
  desc:'A three-wall exhibitor booth with a header and a counter.'},
 {id:'stall',name:'Stall',tag:'Maker market stall',price:'from $269',
  desc:'A counter and tiered display, built for artist alley and markets.'},
 {id:'landing',name:'The Landing',tag:'Entryway shoe rack',price:'from $149',
  desc:'Open shelves by the door, sized for a row of shoes.'},
 {id:'pegwall',name:'Pegwall',tag:'Bag & accessory display',price:'from $169',
  desc:'A pegged panel wall that hangs a collection like artwork.'},
 {id:'wardrobe',name:'Wardrobe',tag:'Open wardrobe',price:'from $299',
  desc:'An open clothing rail with shelves and room to change.'},
 {id:'mudroom',name:'Mudroom',tag:'Coat & entry organiser',price:'from $239',
  desc:'A bench, hooks and upper cubbies for the way in and out.'},
 {id:'riser',name:'Riser',tag:'Desk shelf riser',price:'from $99',
  desc:'A two-tier riser that lifts a monitor and frees the desk.'},
 {id:'headboard',name:'Headboard',tag:'Bed headboard',price:'from $199',
  desc:'A panelled headboard with a cube nightstand on either side.'},
 {id:'crate',name:'Crate',tag:'Vinyl record storage',price:'from $179',
  desc:'Cube bins sized and angled for flicking through records.'},
 {id:'nursery',name:'Nursery',tag:'Plant propagation stand',price:'from $159',
  desc:'An airy tiered stand that gives every plant its share of light.'},
 {id:'storefront',name:'Storefront',tag:'Pop-up shop display',price:'from $329',
  desc:'A wall, shelving and a header sign for retail and pop-ups.'},
 {id:'net',name:'The Net',tag:'Portable sports net',price:'from $199',
  desc:'Two cube towers with a wire mesh strung between. Driveway badminton, packed flat by dinner.'}
];

const look=[
 {cls:'wide',id:'stacks',cap:'Stacks, a working wall in Rozelle',meta:'Editorial photograph'},
 {cls:'tall',id:'paddock',cap:'Paddock, Sunday morning',meta:'Lifestyle photograph'},
 {cls:'tall',id:'booth',cap:'Booth, packed flat for transit',meta:'Location photograph'},
 {cls:'wide',id:'nursery',cap:'Nursery, by the north window',meta:'Editorial photograph'}
];

const configs=[
 {name:'the paddock',id:'paddock'},
 {name:'the landing',id:'landing'},
 {name:'stacks',id:'stacks'},
 {name:'the screen',id:'screen'},
 {name:'the booth',id:'booth'}
];

window.NK=Object.assign(window.NK||{},{isoArt,KIT,kits,look,configs,kitSpecs});
})();
